/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  BORDER_WIDTH,
  type ComponentStyles,
  PADDING_HORIZONTAL_ROUND,
  PADDING_VERTICAL,
} from './WireframeBase';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import tools from '../tools';
import wireframeMultiSelect from '../assets/icons/wireframeMultiSelect.svg';
import { FlexRow, type FlexRowProps } from '@lib/canvasx/components/FlexRow';
import { TextWithIcons } from '../components/wireframes/TextWithIcons';
import wireframeStateRoundHollow from '../assets/icons/wireframeStateRoundHollow.svg';
import wireframeStateSquareHollow from '../assets/icons/wireframeStateSquareHollow.svg';
import { WIREFRAME_SELECTION_COLOR } from '../utils/wireframes';
import WireframeAddItemControl from '../controls/WireframeAddItemControl';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';

const ROUND_RADIUS = 22;
const SQUARE_RADIUS = 4;

class WireframeMultiSelect extends WireframeBase {
  hasDraggableItems = true;

  constructor(fabricObject) {
    super(fabricObject);
    if (this.fabricObject.icons) {
      this.fabricObject.set('showIcons', this.fabricObject.icons);
      this.fabricObject.set('icons', null);
    }
  }

  initItems() {
    if (this.fabricObject.items.items.length === 0) {
      this.fabricObject.items.add({ value: this.defaultText() });
      this.fabricObject.items.add({
        value: this.defaultText(),
        state: 'selected',
      });
      this.fabricObject.items.add({ value: this.defaultText() });
    }
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
      addItem: new WireframeAddItemControl({ layout: 'row' }),
    };
  }

  static iconSvg() {
    return wireframeMultiSelect;
  }

  static externalName(): string {
    return 'Toggle button';
  }

  get requiresPriorSelection() {
    return true;
  }

  get itemBorderRadius() {
    return this.round ? ROUND_RADIUS : SQUARE_RADIUS;
  }

  minDimension() {
    return this.childrenWidth;
  }

  tools() {
    if (this.activeKey) {
      const itemTools = [
        [tools.ToolWireframeItemState],
        [tools.ToolWireframeIcon],
      ];

      // Show delete tool only if more than one item left
      if (this.fabricObject.items.items.length > 1) {
        itemTools.push([tools.ToolWireframeDeleteItem]);
      }

      return itemTools;
    }

    return [
      [tools.ToolWireframeAddItem],
      [tools.ToolWireframeComponentStyle],
      [tools.ToolMultiSelectStyleFlags],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      [tools.ToolWireframeTextColor],
    ];
  }

  supportedIcons(): string[] {
    return ['iconLeft'];
  }

  static componentStyles(): ComponentStyles {
    return {
      round: wireframeStateRoundHollow,
      square: wireframeStateSquareHollow,
    };
  }

  componentStyles() {
    return WireframeMultiSelect.componentStyles();
  }

  wasSet(keyOrPath: KeyPath, value: any) {
    super.wasSet(keyOrPath, value);
    if (keyOrPath === 'borderType') {
      this.fabricObject.set(
        'borderRadius',
        value === 'round' ? ROUND_RADIUS : SQUARE_RADIUS
      );
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const { width, scaleX, textColor, textEditing, showIcons, fontSize } =
      this.fabricObject;
    const fill = this.fabricObject.fill ?? 'transparent';
    const { activeKey } = this;
    const borderRadius = this.round ? ROUND_RADIUS : SQUARE_RADIUS;
    let renderedDimensions;

    const props: Partial<FlexRowProps> = {};

    // Use explicit dimension if user dragged to resize
    if (this.fabricObject.userHasExplicitlySetDimensions()) {
      props.width = width;
    }

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexRow
            border={{ color: fill, lineWidth: BORDER_WIDTH }}
            borderRadius={borderRadius}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            fill={'white'}
            {...props}
          >
            {this.fabricObject.items.items.map(item => {
              const { id, value, state, iconLeft } = item;
              const active = this.activeKey.includes(id);
              const selected = state === 'selected';
              const editingItem = textEditing && activeKey.includes(id);
              const icon = showIcons ? iconLeft : undefined;

              return (
                <FlexColumn
                  key={id}
                  horizontalAlignment='center'
                  fill={selected ? fill : 'transparent'}
                  border={active ? WIREFRAME_SELECTION_COLOR : 'transparent'}
                  borderRadius={borderRadius}
                  flexGrow
                  setRenderedDimensions={dimensions => {
                    item.containerDimensions = dimensions;
                  }}
                >
                  <TextWithIcons
                    textKey={`text:${id}`}
                    padding={{
                      horizontal: PADDING_HORIZONTAL_ROUND,
                      vertical: PADDING_VERTICAL,
                    }}
                    cursor='pointer'
                    color={selected ? textColor : fill}
                    textEditing={editingItem}
                    iconLeft={icon}
                    size={fontSize}
                    onChange={value => {
                      this.onItemChange(value);
                    }}
                    setRenderedDimensions={dimensions => {
                      this.updateMaxItemDimensions({
                        dimensions,
                        useUnwrappedWidth: true,
                      });
                      item.dimensions = dimensions;
                    }}
                  >
                    {value}
                  </TextWithIcons>
                </FlexColumn>
              );
            })}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeMultiSelect;
